import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Map, TrendingUp, ArrowRight, Play, Sparkles, X, Award, Loader2 } from "lucide-react";
import { useLearningMap } from "@/hooks/usePersonalpedia";
import { knowledgeService } from "@/services/knowledgeService";
import { apiError } from "@/lib/api";

const TIERS = {
  Grasshopper: { bar: "bg-emerald-500", text: "text-emerald-600", ring: "ring-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
  Journeyman: { bar: "bg-sky-500", text: "text-sky-600", ring: "ring-sky-500", chip: "bg-sky-50 text-sky-700" },
  Expert: { bar: "bg-amber-500", text: "text-amber-600", ring: "ring-amber-500", chip: "bg-amber-50 text-amber-700" },
  Master: { bar: "bg-rose-500", text: "text-rose-600", ring: "ring-rose-500", chip: "bg-rose-50 text-rose-700" },
};

const TIER_LEGEND = [
  { name: "Grasshopper", range: "0–1h" },
  { name: "Journeyman", range: "1–3h" },
  { name: "Expert", range: "3–8h" },
  { name: "Master", range: "8h+" },
];

export function LearningMap() {
  const { data, isLoading } = useLearningMap();
  const [selected, setSelected] = useState(null);
  const [certTopic, setCertTopic] = useState(null);

  const rows = data?.map || [];
  const maxMin = Math.max(1, ...rows.map((r) => r.minutes));
  const active = rows.find((r) => r.topic === selected);

  const downloadMastery = async (topic) => {
    setCertTopic(topic);
    try {
      await knowledgeService.downloadMasteryCertificate(topic);
      toast.success("Mastery certificate downloaded");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCertTopic(null);
    }
  };

  if (isLoading) {
    return (
      <div className="border border-border bg-card p-6 mb-10" data-testid="learning-map-loading">
        <div className="h-5 w-40 bg-secondary animate-pulse mb-6" />
        {[0, 1, 2].map((i) => <div key={i} className="h-8 bg-secondary animate-pulse mb-3" />)}
      </div>
    );
  }

  return (
    <div className="border border-border bg-card p-6 mb-10" data-testid="learning-map">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Map className="h-4 w-4 text-primary" />
          <h2 className="font-display font-bold">Learning Map</h2>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5 text-primary" /> {data?.total_hours || 0}h across {data?.topics || 0} topics
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-5">Hours invested per topic, by mastery tier. Click a bar to see how to level up.</p>

      {rows.length === 0 ? (
        <div className="text-center py-8" data-testid="learning-map-empty">
          <p className="text-sm text-muted-foreground mb-3">Complete a course to start charting your mastery.</p>
          <Link to="/learn"><button className="text-sm font-semibold text-primary inline-flex items-center gap-1">Create a course <ArrowRight className="h-4 w-4" /></button></Link>
        </div>
      ) : (
        <>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-5">
            {TIER_LEGEND.map((t) => (
              <span key={t.name} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className={`h-2.5 w-2.5 rounded-sm ${TIERS[t.name].bar}`} /> {t.name} <span className="opacity-60">{t.range}</span>
              </span>
            ))}
          </div>

          {/* Bars */}
          <div className="space-y-2.5" data-testid="learning-map-bars">
            {rows.map((r, i) => {
              const tier = TIERS[r.tier] || TIERS.Grasshopper;
              const pct = Math.max(6, Math.round((r.minutes / maxMin) * 100));
              const isSel = selected === r.topic;
              return (
                <button
                  key={r.topic}
                  onClick={() => setSelected(isSel ? null : r.topic)}
                  data-testid={`map-bar-${i}`}
                  className={`w-full text-left group ${isSel ? "ring-2 ring-offset-1 " + tier.ring : ""}`}
                >
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium truncate pr-2">{r.topic}</span>
                    <span className={`shrink-0 font-semibold ${tier.text}`}>{r.hours}h · {r.tier}</span>
                  </div>
                  <div className="h-6 bg-secondary relative overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5, delay: i * 0.05 }}
                      className={`h-full ${tier.bar} group-hover:opacity-90`} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          {active && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mt-6 border-l-2 border-primary bg-secondary/40 p-5" data-testid="map-detail-panel">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-display font-bold text-lg">{active.topic}</h3>
                  <span className={`inline-block mt-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 ${(TIERS[active.tier] || TIERS.Grasshopper).chip}`}>
                    {active.tier} · {active.hours}h · {active.completed_count} course{active.completed_count !== 1 ? "s" : ""}
                  </span>
                </div>
                <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground" data-testid="map-detail-close"><X className="h-4 w-4" /></button>
              </div>

              {active.next_tier ? (
                <p className="text-sm mb-4">
                  <span className="font-semibold text-primary">{active.hours_to_next}h</span> of learning to reach{" "}
                  <span className={`font-semibold ${(TIERS[active.next_tier] || TIERS.Expert).text}`}>{active.next_tier}</span>.
                </p>
              ) : (
                <p className="text-sm mb-4 font-semibold text-rose-600">You've reached Master — the top tier. 🏆</p>
              )}

              {(active.tier === "Expert" || active.tier === "Master") && (
                <div className="mb-5 border border-primary/40 bg-primary/5 p-4" data-testid="mastery-certificate-box">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="h-4 w-4 text-primary" />
                    <span className="font-display font-bold text-sm">{active.tier} mastery unlocked</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">You've earned a mastery certificate for <span className="font-semibold">{active.topic}</span>.</p>
                  <button onClick={() => downloadMastery(active.topic)} disabled={certTopic === active.topic}
                    className="inline-flex items-center gap-2 bg-primary text-white px-4 h-9 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                    data-testid="download-mastery-certificate">
                    {certTopic === active.topic ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />} Download mastery certificate
                  </button>
                </div>
              )}

              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Close the gap</p>
              <div className="space-y-2">
                {active.suggestions.map((s, idx) => (
                  s.generate ? (
                    <Link key={idx} to={`/learn?topic=${encodeURIComponent(s.topic)}&length=comprehensive`}
                      className="flex items-center justify-between border border-dashed border-border bg-card p-3 hover:border-primary transition-colors" data-testid={`map-suggest-${idx}`}>
                      <span className="flex items-center gap-2 text-sm"><Sparkles className="h-4 w-4 text-primary" /> {s.title}</span>
                      <span className="text-xs text-muted-foreground">{s.length_label}</span>
                    </Link>
                  ) : (
                    <Link key={idx} to={`/course/${s.id}`}
                      className="flex items-center justify-between border border-border bg-card p-3 hover:border-primary transition-colors" data-testid={`map-suggest-${idx}`}>
                      <span className="flex items-center gap-2 text-sm min-w-0"><Play className="h-4 w-4 text-primary shrink-0" /> <span className="truncate">{s.title}</span></span>
                      <span className="text-xs text-muted-foreground shrink-0 ml-2">+{(s.minutes / 60).toFixed(1)}h · {s.length_label}</span>
                    </Link>
                  )
                ))}
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
